import { GlobalStore } from '@roc/client-portal-shared/stores';
import { FormStore, UserStore } from '@roc/feature-app-core';
import {
  DateFormat,
  formatDate,
  formatToLocalDateTimeString,
  GENERIC_ERROR_MESSAGE,
  insertIf,
  isNil,
} from '@roc/feature-utils';
import { DialogState } from '@roc/ui';
import {
  action,
  computed,
  flow,
  makeObservable,
  observable,
  override,
} from 'mobx';
import { GeneralTasksService } from '../services/generalTasksService';
import {
  Assignee,
  GeneralTaskAttachment,
  GeneralTaskComment,
  PopulateTaskFormField,
  GeneralTaskType,
  Task,
  TaskStatusDB,
  TaskTypeForm,
  TaskScopeForm,
  TaskV2,
  TaskV2Type,
  SaveTaskV2Request,
  UpdateTaskStatusAndAssigneeRequest,
  TaskCategory,
  TaskV2Category,
  GENERAL_TASK_CODE,
  UpdateReadOnlyTaskStatusAndAssigneeRequest,
} from '../types/generalTasksTypes';
import { appraisalTaskTypes, ASSIGN_TO_NOT_VALID_PRE_MESSAGE, lineItemsTaskTypes, REASSIGN_NOT_VALID_PRE_MESSAGE, taskTypeOptions, TECH_SUPPORT_ID } from '../utils/generalTasksConstants';
import { createEmptyTaskV2, getTaskStatusToDBList, parseTaskStatusDBToStatus, parseTaskStatusToDB } from '../utils/generalTasksUtils';
import { GeneralTasksAttachmentStore } from './generalTasksAttachmentStore';
import { GeneralTasksCommentStore } from './generalTasksCommentStore';
import { GeneralTasksStore } from './generalTasksStore';
import { LoanService } from '@roc/feature-loans';
import { LoanTaskFormStore } from './loanTaskFormStore';
import { TechTaskFormStore } from './techTaskFormStore';
import { NonLoanTaskFormStore } from './nonLoanTaskFormStore';
import { ca } from 'date-fns/locale';

const form = {
  fields: {
    title: {
      value: '',
      error: null,
      rule: 'required',
    },
    description: {
      value: '',
      error: null,
      rule: '',
    },
    status: {
      value: TaskStatusDB.PENDING,
      error: null,
      rule: 'required',
    },
    priority: {
      value: '',
      error: null,
      rule: '',
    },
    dueDate: {
      value: '',
      error: null,
      rule: '',
    },
    assigneeId: {
      value: null,
      error: null,
      rule: [{ required_if: ['assigneeRequired', true] }],
      message: 'The assignee is required.',
    },
    reason: {
      value: null,
      error: null,
      rule: [],
      message: 'The reason is required.',
    },
    exception: {
      value: null,
      error: null,
      rule: [],
      message: 'The exception is required.',
    },
    watchIds: {
      value: null,
      error: null,
      rule: '',
    },
    loanId: {
      value: '',
      error: null,
      rule: 'non_negative',
    },
    taskType: {
      value: '',
      error: null,
      rule: ''
    },
    parentTaskId: {
      value: null,
    },
    ownerName: {
      value: null,
    },
    category: {
      value: '',
      error: null,
      rule: '',
    },
    sectionName: {
      value: null,
      error: null,
      rule: '',
    },
    assigneeRequired: {
      value: false,
      error: null,
      rule: ''
    },
    type: {
      value: TaskTypeForm.LOAN_TASK,
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class EditTaskStore extends FormStore {
  private globalStore: GlobalStore;
  private generalTasksStore: GeneralTasksStore;
  private generalTaskService: GeneralTasksService;
  private loanService: LoanService;

  commentStore: GeneralTasksCommentStore;
  attachmentStore: GeneralTasksAttachmentStore;
  task: Task;
  taskV2: TaskV2;
  dialogState: DialogState;
  comments: GeneralTaskComment[];
  attachments: GeneralTaskAttachment[];

  loanTaskFormStore: LoanTaskFormStore;
  techTaskFormStore: TechTaskFormStore;
  nonLoanTaskFormStore: NonLoanTaskFormStore;

  canEscalateTask: boolean;
  hasConversation: boolean;
  isTamariskAppraisal: boolean;
  isClosingConditionActive: boolean;
  allowedEscalationRoles: string[];
  loanSearchOptions: { label: string; value: string }[] = [];
  isFromSupport: boolean;
  loanTaskSectionNames: TaskCategory[];
  internalValuationReasonDropdown: string[] = [];
  internalValuationCurrentReasonDisplay: string[] = [];

  constructor(
    globalStore: GlobalStore,
    userStore: UserStore,
    generalTasksStore: GeneralTasksStore
  ) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.generalTasksStore = generalTasksStore;
    this.generalTaskService = new GeneralTasksService();
    this.loanService = new LoanService();

    this.commentStore = new GeneralTasksCommentStore(
      globalStore,
      userStore,
      this
    );
    this.attachmentStore = new GeneralTasksAttachmentStore(globalStore, this);
    this.isFromSupport = false;
    this.loanTaskSectionNames = [];
    this.loanTaskFormStore = new LoanTaskFormStore(globalStore);
    this.techTaskFormStore = new TechTaskFormStore(globalStore);
    this.nonLoanTaskFormStore = new NonLoanTaskFormStore(globalStore);

    makeObservable(this, {
      task: observable,
      taskV2: observable,
      hasConversation: observable,
      dialogState: observable,
      comments: observable,
      attachments: observable,
      reset: override,
      closeDialog: action,
      setAttachments: action,
      setComments: action,
      saveTaskWithMessage: flow,
      saveTaskWithMessageNportalIframe: flow,
      loadTask: flow,
      openAddTask: flow,
      openEditTask: flow,
      openAddSubtask: flow,
      saveTask: flow,
      isReadOnly: computed,
      isReadOnlyForAppraisalTasks: computed,
      isReadOnlyBecauseClosingCondition: flow,
      isAppraisalTaskType: computed,
      isSubTask: computed,
      createTaskChatRoom: flow,
      escalateTaskToManager: flow,
      moveTaskToRoc: flow,
      moveTaskToCapitalProvider: flow,
      moveTaskToCMTSpecialist: flow,
      putTaskToTpo: flow,
      getAllowedEscalationRoles: flow,
      canEscalateTask: observable,
      allowedEscalationRoles: observable,
      isTamariskAppraisal: observable,
      isClosingConditionActive: observable,
      mapFormValuesToTask: flow,
      reloadTaskById: flow,
      markTaskAsWaitingOnExternal: flow,
      setIsTamariskAppraisal: flow,
      loanSearchOptions: observable,
      fetchOptions: flow,
      getAutocompleteOptions: flow,
      setIsFromSupport: action,
      addLoanTask: flow,
      loanTaskSectionNames: observable,
      fetchLoanTaskSectionNames: flow,
      changeLoanTaskAssignee: flow,
      changeLoanInternalValuationReportTaskAssignee: flow,
      saveInternalValuationReviewReason: flow,
      internalValuationReasonDropdown: observable,
      fetchInternalValuationReasonDropdown: flow,
      internalValuationCurrentReasonDisplay: observable,
      fetchInternalValuationCurrentReasonDisplay: flow,
      changeLoanReadOnlyTaskAssignee: flow,
      addTaskNote: flow,
    });
  }

  reset() {
    super.reset();
    this.commentStore.reset();
    this.attachmentStore.reset();
    this.task = null;
    this.dialogState = null;
    this.comments = [];
    this.attachments = [];
    this.loanSearchOptions = [];
    this.loanTaskSectionNames = [];
    this.loanTaskFormStore.reset();
    this.techTaskFormStore.reset();
    this.nonLoanTaskFormStore.reset();
    this.taskV2 = {} as TaskV2;
  }

  setIsFromSupport(isFromSupport) {
    this.isFromSupport = isFromSupport;
  }

  *openAddTask(loanId = null, parentTask: TaskV2 = null, populateTaskFormData: PopulateTaskFormField = {}, callback?: () => void) {
    this.reset();
    this.initializeNewTask(parentTask);
    this.generalTasksStore.setLoanId(loanId);
    yield this.loadForm({
      loanId: this.determineNewTaskLoanId(parentTask),
      parentTaskId: parentTask?.taskId,
      ...populateTaskFormData,
      dueDate: null,
    });

    this.dialogState = DialogState.ADD;
    if (callback) callback();
  }

  initializeNewTask(parentTask: TaskV2) {
    this.taskV2 = createEmptyTaskV2();
    this.taskV2.parentTaskId = parentTask?.parentTaskId ?? null;
    this.hasConversation = false;
  }

  determineNewTaskLoanId(parentTask: TaskV2) {
    if (this.isSubTask && parentTask) {
      return parentTask?.typeId1;
    }
    // If the task is created from the loan view loanId will be the current loan
    return this.generalTasksStore.loanId;
  }

  *reloadTaskById(taskId: number, disableGlobalLoading = false) {
    const response = yield this.generalTaskService.getTaskById(taskId, disableGlobalLoading);
    const task = response.data.data;
    yield this.loadTask(task);
  }

  *openEditTask(taskId) {
    this.reset();
    yield this.reloadTaskById(taskId);
    const responseTaskChat = yield this.generalTaskService.doesTaskChatRoomExist(taskId);
    this.hasConversation = responseTaskChat.data.data;
    yield this.generalTasksStore.fetchUserInfoAndAssigneeOptions(this.taskV2?.type1 == TaskV2Type.LOAN_ID ? this.taskV2?.typeId1 : null, null, taskId);
    yield this.generalTasksStore.fetchUserInfoAndWatchersOptions();
    this.dialogState = DialogState.EDIT;
  }

  *createTaskChatRoom(disableGlobalLoading = false) {
    try {
      if (lineItemsTaskTypes.includes(this.taskV2?.category as GeneralTaskType) &&
        (!!this.taskV2?.taskCode && this.taskV2?.taskCode !== GENERAL_TASK_CODE)) {
        yield this.generalTaskService.createLineItemTaskChatRoom(this.taskV2);
      } else {
        yield this.generalTaskService.createTaskChatRoom(this.taskV2);
      }
      const responseTaskChat = yield this.generalTaskService.doesTaskChatRoomExist(this.taskV2?.taskId, disableGlobalLoading);
      this.hasConversation = responseTaskChat.data.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }

  }

  *openAddSubtask(loanId = null) {
    yield this.openAddTask(loanId, this.taskV2);
  }

  *loadTask(task: TaskV2) {
    this.taskV2 = task;
    this.attachments = (this.taskV2?.taskDocuments ?? []).map(document => ({
      taskAttachmentId: document.taskDocumentId,
      taskId: document.taskId,
      documentId: document.taskDocumentId,
      attachmentName: document.originalFileName,
    }));
    this.loadForm({
      title: this.taskV2.title,
      description: this.taskV2?.description,
      status: this.taskV2?.status,
      statusUpdateReason: task?.statusUpdateReason,
      priority: this.taskV2?.priority,
      dueDate: this.taskV2?.dueDate,
      assigneeId: this.taskV2?.taskAssignees?.[0]?.assigneeId,
      loanId: this.taskV2?.typeId1,
      taskType: this.taskV2?.category,
      watchIds: this.taskV2?.taskWatchers?.map(watcher => watcher.userId) ?? [],
      parentTaskId: this.taskV2?.parentTaskId,
      ownerName: this.taskV2?.ownerFirstName + ' ' + this.taskV2?.ownerLastName,
      category: this.taskV2?.category,
    });
    this.fetchOptions(`${this.taskV2?.typeId1}`);
  }

  *saveTask(avoidReassign?: boolean, isTechSupportTask?: boolean) {
    if (this.runFormValidationWithMessage()) {
      const values = this.getFormValues();
      let observers = values.watchIds;

      const taskType = values.taskType ? values.taskType : null;
      const taskCategory = values.category ? values.category : null;

      const taskV2: TaskV2 = {
        ...this.taskV2,
        title: values.title,
        description: values.description,
        status: values.status || null,
        taskQueueStatus: values.status || 'OPEN',
        priority: values.priority || null,
        parentTaskId: values.parentTaskId,
        dueDate: formatToLocalDateTimeString(values.dueDate, 'MM/dd/yyyy HH:mm:ss.SSS'),
        category: taskType || taskCategory,
        type1: isNil(values.loanId) ? null : TaskV2Type.LOAN_ID,
        typeId1: values.loanId,
        taskAssignees: [
          ...insertIf(values.assigneeId, [
            {
              ...((this.taskV2?.taskAssignees?.[0] || {}) as Assignee),
              assigneeId: values.assigneeId,
            },
          ]),
        ],
        observers,
      };
      if (this.taskV2?.typeId1 && !getTaskStatusToDBList().includes(this.taskV2?.status)) {
        const statusChangeResponse = yield this.generalTaskService.isStatusChangeReady(
          this.taskV2?.typeId1,
          values.status
        );
        const statusChange = statusChangeResponse.data?.data;
        if (!statusChange.valid) {
          this.globalStore.notificationStore.showErrorNotification({
            message: statusChange.reason,
          });
          return false;
        }
      }

      if (
        this.taskV2?.typeId1 &&
        values.assigneeId &&
        (!this.taskV2?.taskAssignees.length ||
          this.taskV2.taskAssignees?.[0].assigneeId !== values.assigneeId)
      ) {
        const assigneeChangeResponse = yield this.generalTaskService.isAssigneeChangeValid(
          this.taskV2?.taskId,
          values.assigneeId
        );

        const assigneeChange = assigneeChangeResponse.data?.data;
        if (!assigneeChange.valid) {
          this.globalStore.notificationStore.showErrorNotification({
            message: REASSIGN_NOT_VALID_PRE_MESSAGE + assigneeChange.reason,
          });
          return false;
        }
      }
      taskV2.isInternal = true;
      const buildTaskV2Request: SaveTaskV2Request = {
        taskDetails: {
          ...taskV2,
        },
        assignees: taskV2.taskAssignees,
        observers: taskV2.observers,
        isInternal: taskV2.isInternal,
      };
      let response;
      if (this.globalStore.isLoanChatApp) {
        response = yield this.generalTaskService.saveTaskFromChatApp(
          buildTaskV2Request
        );
      } else {
        response = yield this.generalTaskService.saveTask(
          buildTaskV2Request
        );
      }
      const savedTask = response?.data?.data;
      this.taskV2 = savedTask;
      yield this.generalTasksStore.updateViews();
      return true;
    }
  }

  *changeLoanTaskAssignee(taskId, newAssigneeId, onSuccessCallback) {
    try {
      const stringResponse = yield this.generalTaskService.isAssigneeChangeValid(taskId, newAssigneeId);
      const response = stringResponse.data?.data

      if (!response.valid) {

        this.globalStore.notificationStore.showErrorNotification({
          message: response.reason,
        });

      } else {

        const data = {
          taskId: taskId,
          taskStatus: response.status,
          newAssigneeId: newAssigneeId,
        } as UpdateTaskStatusAndAssigneeRequest

        const changeAssigneeResponse = yield this.generalTaskService.updateTaskStatusAndAssignee(data);
        const success = changeAssigneeResponse.data?.data;

        if (success) {
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Task saved successfully!',
          });
        } else {
          this.globalStore.notificationStore.showErrorNotification({
            message: GENERIC_ERROR_MESSAGE,
          });
        }

        this.closeDialog();
        onSuccessCallback?.();
      }

    } catch (e) {
      console.error(e);
    }
  }

  *changeLoanReadOnlyTaskAssignee(taskId: number, newAssigneeId: number, comment: string, onSuccessCallback: VoidFunction) {
    try {
      const stringResponse = yield this.generalTaskService.isAssigneeChangeValid(taskId, newAssigneeId);
      const response = stringResponse.data?.data

      if (!response.valid) {

        this.globalStore.notificationStore.showErrorNotification({
          message: response.reason,
        });

      } else {

        const data = {
          taskId,
          taskStatus: response.status,
          newAssigneeId: newAssigneeId,
          comment
        } as UpdateReadOnlyTaskStatusAndAssigneeRequest

        const changeAssigneeResponse = yield this.generalTaskService.updateReadOnlyTaskStatusAndAssignee(data);
        const success = changeAssigneeResponse.data?.data;

        if (success) {
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Task saved successfully!',
          });
        } else {
          this.globalStore.notificationStore.showErrorNotification({
            message: GENERIC_ERROR_MESSAGE,
          });
        }

        this.closeDialog();
        onSuccessCallback?.();
      }

    } catch (e) {
      console.error(e);
    }
  }

  *changeLoanInternalValuationReportTaskAssignee(taskId: number, newAssigneeId: number, reason: string, comment: string, onSuccessCallback: VoidFunction, selectedReason?: string) {
    try {
      const stringResponse = yield this.generalTaskService.isAssigneeChangeValid(taskId, newAssigneeId, selectedReason);
      const response = stringResponse.data?.data;

      if (!response.valid) {

        this.globalStore.notificationStore.showErrorNotification({
          message: response.reason,
        });

      } else {

        const data = {
          taskId,
          taskStatus: response.status,
          newAssigneeId,
          reason,
          comment
        } as UpdateTaskStatusAndAssigneeRequest

        const changeAssigneeResponse = yield this.generalTaskService.updateInternalValuationReportTaskStatusAndAssignee(data);
        const success = changeAssigneeResponse.data?.data;

        if (success) {
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Task saved successfully!',
          });
        } else {
          this.globalStore.notificationStore.showErrorNotification({
            message: GENERIC_ERROR_MESSAGE,
          });
        }

        this.closeDialog();
        onSuccessCallback?.();
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchInternalValuationReasonDropdown(loanId: number, propertyId: number) {
    try {
      const response = yield this.generalTaskService.getInternalValuationReasonDropdown(loanId, propertyId);
      this.internalValuationReasonDropdown = response?.data ?? [];
    } catch (e) {
      this.internalValuationReasonDropdown = [];
      console.error(e);
    }
  }

  *fetchInternalValuationCurrentReasonDisplay(loanId: number, propertyId: number) {
    try {
      const response = yield this.generalTaskService.getInternalValuationCurrentReasonDisplay(loanId, propertyId);
      this.internalValuationCurrentReasonDisplay = response?.data ?? [];
    } catch (e) {
      this.internalValuationCurrentReasonDisplay = [];
      console.error(e);
    }
  }

  *saveInternalValuationReviewReason(taskId: number, reason: string) {
    try {
      yield this.generalTaskService.saveInternalValuationReviewReason(taskId, reason);
    } catch (e) {
      console.error(e);
    }
  }

  *saveTaskWithMessage(onSuccessCallback?: () => void, avoidReassign?: boolean, isTechSupportTask?: boolean) {
    try {
      const success = yield this.saveTask(avoidReassign, isTechSupportTask);
      if (success) {
        this.closeDialog();
        onSuccessCallback?.();
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Task saved successfully!',
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *saveTaskWithMessageNportalIframe(onSuccessCallback?: () => void, avoidReassign?: boolean) {
    try {
      const success = yield this.saveTask(avoidReassign);
      if (success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Task saved successfully!',
        });
        onSuccessCallback?.();
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *addLoanTask(onSuccessCallback?: () => void) {
    const editTaskStoreValues = this.getFormValues();
    let body = {};

    if (editTaskStoreValues.type === TaskTypeForm.LOAN_TASK) {
      if (this.loanTaskFormStore.runFormValidationWithMessage()) {

        const values = this.loanTaskFormStore.getFormValues();

        let sectionName = values.sectionName;
        let type2 = values.type2;
        let typeId2 = values.typeId2;

        if (!values.sectionName) {
          const item: TaskCategory | undefined = this.loanTaskSectionNames.find(
            section => section.taskCategoryId === values.sectionId
          );

          sectionName = item?.category ?? GeneralTaskType.LOAN_RELATED_DOCUMENTS;
          typeId2 = item?.typeId2;
          if (item?.borrowerSection === true) {
            type2 = TaskV2Type.BORROWER_ID;
          } else if (item?.propertySection === true) {
            type2 = TaskV2Type.PROPERTY_ID;
          }
        }

        if (!values.taskScope && this.globalStore.isInternalPortal) {
          // Highlight the radio group too; picking an option clears it via onFieldChange.
          this.loanTaskFormStore.form.fields.taskScope.error = 'The task scope is required.';
          this.globalStore.notificationStore.showErrorNotification({
            message: 'Please select a task scope (internal/external)',
          });
        } else {
          body = {
            taskName: values.title,
            description: values.description,
            priority: values.priority,
            loanId: values.loanId,
            sectionName: sectionName,
            internalTask: values.taskScope === TaskScopeForm.INTERNAL,
            assigneeId: values.assigneeId,
            ownerId: this.commentStore?.getUserInfo()?.userId || null,
            observers: values.watchIds,
            type2: type2,
            type_id2: typeId2
          };

          try {
            const response = yield this.generalTaskService.addLoanTask(body, this.globalStore.isLoanChatApp);

            yield this.attachmentStore.uploadPendingAttachments(response?.data?.data?.taskId);

            this.globalStore.notificationStore.showSuccessNotification({
              message: 'Task created successfully.',
            });

            onSuccessCallback?.();
          } catch (e) {
            console.log('e', e);
            this.globalStore.notificationStore.showErrorNotification({
              message: 'There was an error creating the task.',
            });
          }
        }
      } else {
        console.log('loan task not ready')
        return;
      }

    } else if (editTaskStoreValues.type === TaskTypeForm.TECH_SUPPORT) {
      if (this.techTaskFormStore.runFormValidationWithMessage()) {
        const values = this.techTaskFormStore.getFormValues();
        const taskV2: TaskV2 = {
          ...this.taskV2,
          title: values.title,
          description: values.description,
          status: parseTaskStatusToDB(values.status) || TaskStatusDB.PENDING,
          priority: values.priority || null,
          parentTaskId: values.parentTaskId,
          dueDate: null,
          isInternal: values.taskScope === TaskScopeForm.INTERNAL,
          type1: values.loanId ? TaskV2Type.LOAN_ID : undefined,
          typeId1: values.loanId,
          category: TaskV2Category.TECH_SUPPORT,
          taskAssignees: [
            {
              ...(({}) as Assignee),
              assigneeId: TECH_SUPPORT_ID,
            },
          ],
          observers: values.watchIds,
        };
        try {
          const buildTaskV2Request: SaveTaskV2Request = {
            taskDetails: {
              ...taskV2,
            },
            assignees: taskV2.taskAssignees,
            observers: taskV2.observers,
            isInternal: taskV2.isInternal
          }

          let success;

          if (this.globalStore.isLoanChatApp) {
            success = yield this.generalTaskService.saveTaskFromChatApp(buildTaskV2Request);
          } else {
            success = yield this.generalTaskService.saveTask(buildTaskV2Request);
          }
          if (success) {
            yield this.attachmentStore.uploadPendingAttachments(success?.data?.data?.taskId);

            this.globalStore.notificationStore.showSuccessNotification({
              message: 'Task saved successfully!',
            });
            this.closeDialog();
            onSuccessCallback?.();
          }
        } catch (e) {
          this.globalStore.notificationStore.showErrorNotification({
            message: GENERIC_ERROR_MESSAGE,
          });
        }
      } else {
        console.log('tech task not ready')
        return;
      }
    }
    else if (editTaskStoreValues.type === TaskTypeForm.GENERAL_TASK) {
      if (this.nonLoanTaskFormStore.runFormValidationWithMessage()) {

        const values = this.nonLoanTaskFormStore.getFormValues();

        console.log("General task Type: ", values);

        const taskV2: TaskV2 = {
          ...this.taskV2,
          title: values.title,
          description: values.description,
          status: parseTaskStatusToDB(values.status) || TaskStatusDB.PENDING,
          priority: values.priority || null,
          parentTaskId: values.parentTaskId,
          dueDate: null,
          isInternal: values.taskScope === TaskScopeForm.INTERNAL,
          type1: values.loanId ? TaskV2Type.LOAN_ID : undefined,
          typeId1: values.loanId,
          category: values.category,
          taskAssignees: [
            {
              ...(({}) as Assignee),
              assigneeId: values.assigneeId,
            },
          ],
          observers: values.watchIds,
        };
        try {
          const buildTaskV2Request: SaveTaskV2Request = {
            taskDetails: {
              ...taskV2,
            },
            assignees: taskV2.taskAssignees,
            observers: taskV2.observers,
            isInternal: taskV2.isInternal
          }

          let success;
          if (this.globalStore.isLoanChatApp) {
            success = yield this.generalTaskService.saveTaskFromChatApp(buildTaskV2Request);
          } else {
            success = yield this.generalTaskService.saveTask(buildTaskV2Request);
          }

          if (success) {
            yield this.attachmentStore.uploadPendingAttachments(success?.data?.data?.taskId);

            this.globalStore.notificationStore.showSuccessNotification({
              message: 'Task saved successfully!',
            });
            this.closeDialog();
            onSuccessCallback?.();
          }
        } catch (e) {
          console.log('e', e);
          this.globalStore.notificationStore.showErrorNotification({
            message: GENERIC_ERROR_MESSAGE,
          });
        }
      } else {
        console.log('non loan task not ready')
        return;
      }
    }

  }

  setComments(comments) {
    this.comments = comments;
  }

  setAttachments(attachments) {
    this.attachments = attachments;
  }

  setHasConversation(hasConversation) {
    this.hasConversation = hasConversation;
  }

  *deleteTask(taskId) {
    //yield this.generalTaskService.
  }

  closeDialog() {
    this.dialogState = null;
    this.generalTasksStore.resetAndFetchOptions();
  }

  get isEscalationTask() {
    return this.form.fields.taskType.value == GeneralTaskType.ESCALATION;
  }

  get isGeneralTask() {
    return this.taskV2.taskCode === GENERAL_TASK_CODE;
  }

  get isReadOnly() {
    const taskType = this.taskV2?.category;
    const isSystemTask = this.taskV2?.isSystemTask;
    const isLegacyTaskType = taskTypeOptions.some(opt => opt.value === taskType && opt.legacy);
    return isLegacyTaskType || isSystemTask;
  }

  get isReadOnlyForAppraisalTasks() {
    return (this.globalStore.isCrmPortal || this.globalStore.isLenderPortal)
      && appraisalTaskTypes.includes(this.taskV2?.category as GeneralTaskType);
  }

  *isReadOnlyBecauseClosingCondition(taskId: number) {
    const response = yield this.generalTaskService.hasClosingCondition(taskId);
    this.isClosingConditionActive = response.data?.data;
  }

  get isAppraisalTaskType() {
    return appraisalTaskTypes.includes(this.taskV2?.category as GeneralTaskType);
  }

  get isSubTask() {
    return !!this.task?.parentTask;
  }

  *getAllowedEscalationRoles() {
    const response = yield this.generalTaskService.getAllowedEscalationRoles();
    const allowedEscalationRoles: string[] = response.data.data;
    return allowedEscalationRoles;
  }

  *escalateTaskToManager() {
    try {
      const response = yield this.generalTaskService.escalateTaskToManager(this.taskV2?.taskId);

      const savedTask = response.data.data;
      this.taskV2 = savedTask;
      yield this.loadTask(this.taskV2);
      yield this.generalTasksStore.updateViews();

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Task escalated successfully!',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *markTaskAsCompletedV2(onFinish: () => void) {
    try {
      yield this.generalTaskService.markTaskAsCompletedV2(this.taskV2?.taskId);
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      onFinish();
    }
  }

  *markTaskAsMoreInfoNeeded() {
    try {
      yield this.generalTaskService.markTaskAsNotApplicable(this.taskV2?.taskId);
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *markTaskAsWaitingOnExternal(onFinish: () => void) {
    try {
      yield this.generalTaskService.markTaskAsWaitingOnExternal(this.taskV2?.typeId1, this.taskV2.typeId2, this.taskV2?.category);
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      onFinish();
    }
  }

  *moveTaskToCapitalProvider(onFinish: () => void) {
    try {
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      onFinish();
    }
  }

  *moveTaskToRoc(onFinish: () => void) {
    try {
      yield this.generalTaskService.moveTaskToRoc(this.taskV2.taskId);
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      onFinish();
    }
  }

  *moveTaskToCMTSpecialist(onFinish: () => void) {
    try {
      yield this.generalTaskService.moveTaskToCMTSpecialist(this.taskV2.taskId);
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      onFinish();
    }
  }

  *putTaskToTpo(onFinish: () => void) {
    try {
      yield this.generalTaskService.putTaskToTpo(this.taskV2.taskId);
      yield this.generalTasksStore.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      onFinish();
    }
  }

  *mapFormValuesToTask(values) {
    const task: TaskV2 = {
      ...this.task,
      title: values.title,
      description: values.description,
      status: parseTaskStatusToDB(values.status) || null,
      priority: values.priority || null,
      parentTaskId: values.parentTaskId,
      dueDate: formatDate(values.dueDate, DateFormat.MMDDYYYY),
      typeId1: values.loanId,
      category: values.taskType ? values.taskType : null,
      taskAssignees: [
        ...insertIf(values.assigneeId, [
          {
            ...((this.task.assignees?.[0] || {}) as Assignee),
            assigneeId: values.assigneeId,
          },
        ]),
      ],
      observers: values.watchIds
    };

    return task;
  }

  *setIsTamariskAppraisal(loanId: number, propertyAppraisalId: number) {
    const response = yield this.generalTaskService.getIsTamariskAppraisal(loanId, propertyAppraisalId);
    this.isTamariskAppraisal = response.data.data
  }

  *fetchOptions(searchText: string) {
    try {
      if (!searchText) {
        this.loanSearchOptions = [];
        return;
      }
      const options = yield this.getAutocompleteOptions(searchText);
      this.loanSearchOptions = options;
    } catch (e) {
      console.log(e);
      this.loanSearchOptions = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the list of loans.',
      });
    }
  }

  *getAutocompleteOptions(searchText: string) {
    const filters = { dealid: searchText };
    const response = yield this.loanService.autoCompleteLoanIds(
      1,
      25,
      null,
      null,
      filters,
      true
    );
    const rows = JSON.parse(response?.data?.data)?.rows;
    return rows.map(row => ({
      label: `${row.dealid}`,
      value: row.dealid,
    }));
  }

  showWarning(customMessage) {
    if (this.globalStore) {
      this.globalStore.notificationStore.showWarningNotification({
        message: customMessage
      });
      return false;
    }
  }

  *fetchLoanTaskSectionNames(loanId: number, loanProcess: string) {
    try {
      const response = yield this.generalTaskService.getLoanTaskSectionNames(loanId, loanProcess);
      this.loanTaskSectionNames = response?.data?.data ?? [];
    } catch (e) {
      console.error(e);
    }
  }

  *addTaskNote(taskId: number, comment: string, onFinish: () => void) {
    try {
      const response = yield this.generalTaskService.addTaskNote(taskId, comment);

      this.taskV2.taskNotes = [response?.data.data, ...this.taskV2.taskNotes];
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Comment saved successfully.',
      });
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error saving the comment, please try again.',
      });
    } finally {
      onFinish();
    }
  }
}
